import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://orizom.com.br";
const SITE_NAME = "Orizom";
const DEFAULT_TITLE = "Orizom";
const ADMIN_TITLE = "Orizom Admin";
const DEFAULT_FAVICON = "/favicon.ico";
const ADMIN_FAVICON = "/favicon-admin.ico?v=admin-red";
const DEFAULT_DESCRIPTION =
  "A Orizom conecta franquias, catalogo, pedidos, produtos e rotinas operacionais em uma plataforma fechada para operacoes de marketplace.";
const DEFAULT_IMAGE = `${SITE_URL}/img/logo-orizom.svg`;

const INDEXABLE_ROUTES = {
  "/": {
    title: "Orizom",
    description:
      "Plataforma fechada para franqueados Orizom venderem, acompanharem pedidos e operarem catalogo, marketplaces e rotinas comerciais com mais controle.",
    canonicalPath: "/",
    schema: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Orizom",
      url: SITE_URL,
      logo: `${SITE_URL}/logo512.png`,
      sameAs: [],
    },
  },
  "/cadastro": {
    title: "Seja franqueado Orizom | Cadastro",
    description:
      "Cadastre-se para iniciar sua jornada como franqueado Orizom e acessar a plataforma fechada de operacao para marketplaces.",
    canonicalPath: "/cadastro",
  },
  "/termos-de-uso": {
    title: "Termos de Uso | Orizom",
    description:
      "Conheca as regras, responsabilidades e condicoes de uso da plataforma Orizom.",
    canonicalPath: "/termos-de-uso",
  },
  "/politica-de-privacidade": {
    title: "Politica de Privacidade | Orizom",
    description:
      "Entenda como a Orizom trata dados pessoais e protege informacoes dentro da plataforma.",
    canonicalPath: "/politica-de-privacidade",
  },
};

const ROUTE_ALIASES = {
  "/signup": "/cadastro",
  "/privacidade": "/politica-de-privacidade",
  "/teste": "/",
};

const FORCE_NOINDEX_PREFIXES = [
  "/admin",
  "/catalogo",
  "/zpluma",
  "/login",
  "/logout",
  "/purchase",
  "/erro-bling",
  "/produtos",
  "/pedidos",
  "/dashboard",
  "/integracoes",
  "/pagamento",
  "/renovacao",
  "/banido",
];

const ensureMeta = (selector, createAttributes = {}) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(createAttributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }
  return element;
};

const setMeta = (name, content) => {
  const element = ensureMeta(`meta[name="${name}"]`, { name });
  element.setAttribute("content", content);
};

const setPropertyMeta = (property, content) => {
  const element = ensureMeta(`meta[property="${property}"]`, { property });
  element.setAttribute("content", content);
};

const setCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const setFavicon = (href) => {
  let element = document.head.querySelector('link[rel="icon"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "icon");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

const setJsonLd = (schema) => {
  const id = "orizom-jsonld";
  let element = document.getElementById(id);

  if (!schema) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(schema);
};

const getRouteSeo = (pathname) => {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const canonicalRoute = ROUTE_ALIASES[normalizedPath] || normalizedPath;
  const config = INDEXABLE_ROUTES[canonicalRoute];
  const forcedNoindex = FORCE_NOINDEX_PREFIXES.some((prefix) =>
    normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  );

  if (!config || forcedNoindex) {
    const isAdminRoute = normalizedPath === "/admin" || normalizedPath.startsWith("/admin/");

    return {
      title: isAdminRoute ? ADMIN_TITLE : DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      canonicalUrl: `${SITE_URL}${canonicalRoute}`,
      favicon: isAdminRoute ? ADMIN_FAVICON : DEFAULT_FAVICON,
      robots: "noindex, nofollow",
      schema: null,
    };
  }

  const isAlias = canonicalRoute !== normalizedPath;

  return {
    title: config.title,
    description: config.description,
    canonicalUrl: `${SITE_URL}${config.canonicalPath}`,
    favicon: DEFAULT_FAVICON,
    robots: isAlias ? "noindex, follow" : "index, follow",
    schema: config.schema || null,
  };
};

export default function SeoHead() {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return;

    const seo = getRouteSeo(location.pathname);

    document.documentElement.setAttribute("lang", "pt-BR");
    document.title = seo.title;

    setMeta("description", seo.description);
    setMeta("robots", seo.robots);
    setMeta("googlebot", seo.robots);
    setMeta("author", SITE_NAME);
    setMeta("application-name", SITE_NAME);

    setCanonical(seo.canonicalUrl);
    setFavicon(seo.favicon);

    setPropertyMeta("og:locale", "pt_BR");
    setPropertyMeta("og:type", "website");
    setPropertyMeta("og:site_name", SITE_NAME);
    setPropertyMeta("og:title", seo.title);
    setPropertyMeta("og:description", seo.description);
    setPropertyMeta("og:url", seo.canonicalUrl);
    setPropertyMeta("og:image", DEFAULT_IMAGE);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", seo.title);
    setMeta("twitter:description", seo.description);
    setMeta("twitter:image", DEFAULT_IMAGE);

    setJsonLd(seo.schema);
  }, [location.pathname]);

  return null;
}
